import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import DatePicker from 'react-datepicker';
import ExternalModal from '../_common/ExternalModal';
import Spinner from '../_common/Spinner';
import {getSensorGroups} from '../../store/actions/sensorGroupsActions';
import {downloadSensorGroupReportPdf} from '../../store/actions/sensorGroupReportsActions';
import '../WeatherReports.scss';
import './SensorGroupReports.scss';

const selector = state => ({
    t: state.language.translator,
    groups: state.sensorGroups.groups,
    groupsPending: state.sensorGroups.pending,
    groupsError: state.sensorGroups.error
});

const formatDate = date => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const SensorGroupReports = () => {
    const dispatch = useDispatch();
    const {t, groups, groupsPending, groupsError} = useSelector(selector);
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [currentFilter, setCurrentFilter] = useState(null);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [pendingGroups, setPendingGroups] = useState([]);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState('');
    const reportRequest = useRef(null);

    useEffect(() => {
        dispatch(getSensorGroups()).catch(() => {});
    }, [dispatch]);

    useEffect(() => () => {
        if (reportRequest.current) {
            reportRequest.current.abort();
        }
    }, []);

    const openGroupModal = () => {
        setPendingGroups([...selectedGroups]);
        setShowGroupModal(true);
    };

    const confirmGroups = () => {
        setSelectedGroups(pendingGroups);
        setShowGroupModal(false);
        setDownloadError('');
    };

    const toggleGroup = group => {
        setPendingGroups(current =>
            current.some(selected => selected.id === group.id)
                ? current.filter(selected => selected.id !== group.id)
                : [...current, group]
        );
    };

    const downloadReport = () => {
        if (selectedGroups.length === 0 || !dateFrom || !dateTo || dateFrom > dateTo) {
            return;
        }

        setDownloading(true);
        setDownloadError('');
        const controller = new AbortController();
        reportRequest.current = controller;
        dispatch(downloadSensorGroupReportPdf({
            sensorGroupIds: selectedGroups.map(group => group.id),
            dateFrom: formatDate(dateFrom),
            dateTo: formatDate(dateTo)
        }, controller.signal))
            .catch(error => {
                if (error.name !== 'AbortError') {
                    setDownloadError(error.message || t.gettext('Unable to download sensor group report'));
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setDownloading(false);
                    reportRequest.current = null;
                }
            });
    };

    const canDownload = selectedGroups.length > 0 && dateFrom && dateTo && dateFrom <= dateTo && !downloading;

    return (
        <div className='weatherReports sensorGroupReports'>
            <div className='weatherReportsHeader'>
                <div className='sensorGroupReportTitle'>
                    <span>{t.gettext('Sensor group reports')}</span>
                    {selectedGroups.length > 0 &&
                        <span className='sensorGroupNames'>
                            {selectedGroups.map(group => group.groupName).join(', ')}
                        </span>
                    }
                </div>
                <div className='selectStationBtn' onClick={openGroupModal}>
                    {t.gettext('Select sensor group')}
                </div>
            </div>

            <div className='weatherReportsFilters'>
                <div
                    className='filterContainer'
                    onClick={() => currentFilter !== 'dateFrom' && setCurrentFilter('dateFrom')}
                >
                    <span className='filterType'>{t.gettext('Date from')}:</span>
                    <span className='selectedFilter'>&nbsp;{dateFrom ? dateFrom.toLocaleDateString() : ''}</span>
                    {currentFilter === 'dateFrom' &&
                        <DatePicker
                            dateFormat='dd/MM/yyyy'
                            selected={dateFrom}
                            maxDate={dateTo}
                            inline
                            onChange={date => {
                                setDateFrom(date);
                                setCurrentFilter(null);
                                setDownloadError('');
                            }}
                        />
                    }
                </div>
                <div
                    className='filterContainer'
                    onClick={() => currentFilter !== 'dateTo' && setCurrentFilter('dateTo')}
                >
                    <span className='filterType'>{t.gettext('Date to')}:</span>
                    <span className='selectedFilter'>&nbsp;{dateTo ? dateTo.toLocaleDateString() : ''}</span>
                    {currentFilter === 'dateTo' &&
                        <DatePicker
                            dateFormat='dd/MM/yyyy'
                            selected={dateTo}
                            minDate={dateFrom}
                            inline
                            onChange={date => {
                                setDateTo(date);
                                setCurrentFilter(null);
                                setDownloadError('');
                            }}
                        />
                    }
                </div>
                <div className='reportBtnContainer'>
                    <button
                        type='button'
                        className='downloadReportBtn'
                        disabled={!canDownload}
                        onClick={downloadReport}
                    >
                        {downloading &&
                            <span className='sensorGroupReportLoader' aria-hidden='true'/>
                        }
                        {downloading ? t.gettext('Generating PDF...') : t.gettext('PDF report')}
                    </button>
                </div>
            </div>

            {downloadError && <div className='sensorGroupReportError'>{downloadError}</div>}

            {showGroupModal &&
                <ExternalModal
                    closeMethod={() => setShowGroupModal(false)}
                    title={t.gettext('Select sensor group')}
                >
                    <div className='sensorGroupPicker'>
                        {groupsPending && groups.length === 0
                            ? <Spinner size={150}/>
                            : groupsError
                                ? <div className='sensorGroupReportError'>{t.gettext('Unable to load sensor groups')}</div>
                                : groups.length === 0
                                    ? <div>{t.gettext('No sensor groups')}</div>
                                    : groups.map(group =>
                                        <label className='sensorGroupPickerOption' key={group.id}>
                                            <input
                                                type='checkbox'
                                                checked={pendingGroups.some(selected => selected.id === group.id)}
                                                onChange={() => toggleGroup(group)}
                                            />
                                            <span>{group.groupName}</span>
                                            <span className='sensorGroupPickerCount'>
                                                {(group.sensorIds || []).length} {t.gettext('sensors')}
                                            </span>
                                        </label>
                                    )
                        }
                        <div className='sensorGroupPickerActions'>
                            <button type='button' className='secondaryButton' onClick={() => setShowGroupModal(false)}>
                                {t.gettext('Cancel')}
                            </button>
                            <button
                                type='button'
                                className='primaryButton'
                                disabled={pendingGroups.length === 0}
                                onClick={confirmGroups}
                            >
                                {t.gettext('Select')}
                            </button>
                        </div>
                    </div>
                </ExternalModal>
            }
        </div>
    );
};

export default SensorGroupReports;
